import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../../../core/context/useAuthContext";
import useApi from "../../../core/customhook/useApi";
import Swal from "sweetalert2";
import z from "zod";
import SnipperLoader from "../../../core/common/loader/SnipperLoader";
import { apibaseurl } from "../../../core/webservice/Webservice_url";

interface TatDetailsProps {
  projectId: string;
}

const TatDetails: React.FC<TatDetailsProps> = ({ projectId }) => {
  const { auth } = useAuth();
  const orgId = auth?.orgId;

  const getTatSetting = useApi<any>(
    `/api/v1/master/organizations/${orgId}/projects/${projectId}/tat-settings`,
    {
      method: "GET",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const gettatLoading = getTatSetting.loading;

  const updateTatSetting = useApi<any>(
    `/api/v1/master/organizations/${orgId}/projects/${projectId}/tat-settings`,
    {
      method: "PUT",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const tatSchema = z
    .object({
      tat_type: z
        .string()
        .refine((val) => val === "case_wise" || val === "check_wise", {
          message: "Please select a valid TAT type.",
        }),

      case_wise_tat_days: z.any(),

      start_case_on_insufficiency: z.boolean(),

      tat_day_type: z
        .string()
        .refine((val) => val === "working_days" || val === "calendar_days", {
          message: "Please select a valid Day Type.",
        }),

      sla_cutoff_time: z.string().min(1, "SLA Cut Off Time is required."),

      insufficiency_counted_in_tat: z.boolean(),
    })
    .superRefine((data, ctx) => {
      if (data.tat_type === "case_wise") {
        if (!data.case_wise_tat_days || Number(data.case_wise_tat_days) <= 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["case_wise_tat_days"],
            message: "Case Wise TAT Days is required.",
          });
        }
      }
    });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [settings, setSettings] = useState<any>(null);
  const [sysData, setSysData] = useState<any>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [authorizedSignature, setAuthorizedSignature] = useState<File | null>(
    null,
  );

  const [authorizedImage, setAuthorizedImage] = useState<string>("");
  useEffect(() => {
    if (!projectId || !orgId) return;

    fetchTatSettings();
  }, [projectId, orgId]);

  useEffect(() => {
    if (settings?.tat_type === "check_wise") {
      setSettings((prev: any) => ({
        ...prev,
        case_wise_tat_days: null,
      }));
    }
  }, [settings?.tat_type]);

  const fetchTatSettings = async () => {
    try {
      const res = await getTatSetting.callApi();

      if (res?.success) {
        const data = res.data;

        setSettings(data.settings);
        setSysData(data.sys_data);
        setAuthorizedImage(data?.settings?.authorized_image ?? "");
      }
    } catch {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Unable to load TAT settings.",
      });
    }
  };

  const [previewUrl, setPreviewUrl] = useState<string>("");

  useEffect(() => {
    if (!authorizedSignature) {
      setPreviewUrl("");
      return;
    }
    const url = URL.createObjectURL(authorizedSignature);
    setPreviewUrl(url);

    return () => URL.revokeObjectURL(url);
  }, [authorizedSignature]);

  const handleSave = async () => {
    setErrors({});

    const result = tatSchema.safeParse(settings);

    if (!result.success) {
      const fieldErrors: Record<string, string> = {};

      result.error.issues.forEach((err) => {
        fieldErrors[err.path[0] as string] = err.message;
      });

      setErrors(fieldErrors);

      Swal.fire({
        icon: "warning",
        title: "Validation Error",
        text: "Please correct the highlighted fields.",
      });

      return;
    }

    try {
      const formData = new FormData();

      Object.entries(result.data).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          formData.append(key, String(value));
        }
      });

      if (authorizedSignature) {
        formData.append("authorized_image", authorizedSignature);
      }

      const res = await updateTatSetting.callApi(formData);

      if (!res?.success) {
        Swal.fire({
          icon: "error",
          title: "Update Failed",
          text: res?.error || "Unable to update TAT settings.",
        });

        return;
      }

      Swal.fire({
        icon: "success",
        title: "Success",
        text: "TAT settings updated successfully.",
      });

      setAuthorizedSignature(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      await fetchTatSettings();
    } catch (error: any) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error?.response?.data?.error ||
          error?.error ||
          "Something went wrong.",
      });
    }
  };

  if (!projectId) {
    return (
      <div className="card shadow-sm border-0">
        <div
          className="card-body text-center d-flex flex-column justify-content-center"
          style={{ minHeight: 350 }}
        >
          <h5 className="mt-4 mb-2 fw-bold">Select Brand Name</h5>
          <p className="text-muted mb-0">
            Select a brand above to view and configure its turnaround time
            settings.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      {gettatLoading ? (
        <SnipperLoader />
      ) : (
        <div className="card border-0 shadow-sm">
          <div className="card-body p-4">
            <h5 className="fw-bold mb-4">TAT Setup</h5>

            {/* TAT Setting */}
            <div className="mb-5">
              <p className="fw-semibold mb-1 text-dark">Select TAT Settings</p>

              <div className="row">
                {sysData?.tat_type?.map((item: any) => (
                  <div className="col-md-6" key={item.value}>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="tat_type"
                        id={item.value}
                        value={item.value}
                        checked={settings?.tat_type === item.value}
                        onChange={(e) =>
                          setSettings((prev: any) => ({
                            ...prev,
                            tat_type: e.target.value,
                          }))
                        }
                      />

                      <label className="form-check-label" htmlFor={item.value}>
                        {item.label}
                      </label>
                    </div>
                  </div>
                ))}
              </div>

              <div className="row mt-3">
                <div className="col-md-4">
                  <label className="form-label fw-semibold">
                    Case Wise TAT Days
                  </label>

                  <input
                    type="number"
                    className={`form-control ${
                      errors.case_wise_tat_days ? "is-invalid" : ""
                    }`}
                    placeholder="Enter Case Wise TAT Days"
                    value={settings?.case_wise_tat_days ?? ""}
                    disabled={settings?.tat_type !== "case_wise"}
                    onChange={(e) =>
                      setSettings((prev: any) => ({
                        ...prev,
                        case_wise_tat_days:
                          e.target.value === "" ? null : Number(e.target.value),
                      }))
                    }
                  />

                  {errors.case_wise_tat_days && (
                    <div className="invalid-feedback">
                      {errors.case_wise_tat_days}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Start Case */}
            <div className="mb-5">
              <p className="fw-semibold text-dark mb-1">
                Would you like to start the case if one check is insuff?
              </p>

              <div className="row">
                {sysData?.start_case_on_insufficiency?.map((item: any) => (
                  <div className="col-md-6" key={String(item.value)}>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="start_case_on_insufficiency"
                        id={`start_case_${item.value}`}
                        checked={
                          settings?.start_case_on_insufficiency === item.value
                        }
                        onChange={() =>
                          setSettings((prev: any) => ({
                            ...prev,
                            start_case_on_insufficiency: item.value,
                          }))
                        }
                      />

                      <label
                        className="form-check-label"
                        htmlFor={`start_case_${item.value}`}
                      >
                        {item.label}
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Insufficiency Settings */}
            <div className="mb-5">
              <p className="fw-semibold mb-1 text-dark">
                TAT settings for Insufficiency (Incomplete Information or
                Document)
              </p>

              <div className="row">
                {sysData?.insufficiency_counted_in_tat?.map((item: any) => (
                  <div className="col-md-6" key={String(item.value)}>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="insufficiency_counted_in_tat"
                        id={`insuff_${item.value}`}
                        checked={
                          settings?.insufficiency_counted_in_tat === item.value
                        }
                        onChange={() =>
                          setSettings((prev: any) => ({
                            ...prev,
                            insufficiency_counted_in_tat: item.value,
                          }))
                        }
                      />

                      <label
                        className="form-check-label"
                        htmlFor={`insuff_${item.value}`}
                      >
                        {item.label}
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <div className="mb-4">
                  <h5 className="fw-semibold mb-4">Case</h5>

                  <div className="row">
                    <div className="col-md-8">
                      <label className="form-label fw-semibold">
                        Working Days / Calendar Days
                        <span className="text-danger"> *</span>
                      </label>

                      <div className="d-flex gap-3 mt-2">
                        {sysData?.tat_day_type?.map((item: any) => (
                          <button
                            key={item.value}
                            type="button"
                            className={`btn ${
                              settings?.tat_day_type === item.value
                                ? "btn-primary"
                                : "btn-outline-secondary"
                            }`}
                            onClick={() =>
                              setSettings((prev: any) => ({
                                ...prev,
                                tat_day_type: item.value,
                              }))
                            }
                          >
                            {item.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="row mt-4">
                      <div className="col-md-4">
                        <label className="form-label fw-semibold">
                          SLA Cut Off Time
                          <span className="text-danger"> *</span>
                        </label>

                        <input
                          type="time"
                          className="form-control"
                          value={settings?.sla_cutoff_time?.slice(0, 5) || ""}
                          onChange={(e) =>
                            setSettings((prev: any) => ({
                              ...prev,
                              sla_cutoff_time: `${e.target.value}:00`,
                            }))
                          }
                        />

                        <small className="text-muted">
                          Time after which the next working day SLA will be
                          considered.
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-4">
                  <h5 className="fw-semibold mb-4">Authorized Signature</h5>
                  <label className="form-label fw-semibold">
                    Upload Authorized Signature
                  </label>

                  <input
                    type="file"
                    ref={fileInputRef}
                    className="form-control"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0] || null;
                      setAuthorizedSignature(file);
                    }}
                  />
                  {(previewUrl || authorizedImage) && (
                    <div className="mt-3">
                      <label className="form-label fw-semibold">
                        {previewUrl
                          ? "Selected Signature (not saved yet)"
                          : "Current Authorized Signature"}
                      </label>

                      <div>
                        <img
                          src={
                            previewUrl ||
                            `${apibaseurl}${authorizedImage}?t=${Date.now()}`
                          }
                          alt="Authorized Signature"
                          style={{
                            width: "130px",
                            height: "100px",
                            objectFit: "contain",
                            border: "1px solid #ddd",
                            borderRadius: "6px",
                            padding: "5px",
                            backgroundColor: "#fff",
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="d-flex justify-content-end">
              <button
                className="btn btn-primary"
                onClick={handleSave}
                disabled={updateTatSetting.loading}
              >
                {updateTatSetting.loading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                    />
                    Saving...
                  </>
                ) : (
                  "Save Setup"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TatDetails;
